import { useEffect } from 'react';
import { Formik } from 'formik';
import {
  string as yupString,
  object as yupObject,
  mixed,
  boolean as yupBool,
  array,
} from 'yup';
import isURL from 'validator/lib/isURL';
import Fields from './Fields';
import useStyles from './styles';
import { AdFormValues } from './types';
import { AdStartDate } from 'types/advertising';
import { CampaignType } from 'types/campaign';
import type { CampaignInfoStepProps } from 'organisms/CampaignSteps/CampaignInfoStep';
import { OriginalAdDesignCost } from 'config/Ads';

export type { AdFormValues } from './types';

export type AdFormPropTypes = CampaignInfoStepProps & {
  campaignTypes: Array<CampaignType>;
  /** When true, Back/Next sit in a footer pinned to the bottom on mobile (campaign flow). */
  stickyActionsFooter?: boolean;
  /** When false, hides the running total row (checkout step shows OrderSummary). */
  showTotal?: boolean;
};

/**
 * Calculates the initial campaign cost based on campaign type and design provided status.
 *
 * @param campaign - The campaign form values
 * @param campaignTypes - Array of available campaign types
 * @returns The calculated initial cost in cents
 */
const calculateInitialCampaignCost = (
  campaign: AdFormValues,
  campaignTypes: Array<CampaignType>,
): number => {
  const defaultType = campaignTypes[0];
  let cost = defaultType.cost;

  if (campaign.campaignType) {
    const selectedType = campaignTypes.find(
      (type) => type.typeId === campaign.campaignType,
    );
    if (selectedType) {
      cost = selectedType.cost;
      if (!campaign.designProvided) {
        cost += OriginalAdDesignCost;
      }
    }
  }

  return cost;
};

/**
 * AdForm Component
 *
 * Form component for creating and editing advertising campaigns. Handles campaign
 * details including name, type, start date, design source, file uploads, and click-through URL.
 * Includes dynamic cost calculation based on campaign type and design options.
 *
 * Features:
 * - Form validation using Yup schema
 * - Dynamic cost calculation based on campaign type and design provided status
 * - File upload support for ad assets (.zip with multiple sizes or creative assets)
 * - URL validation for click-through links
 * - Integration with Formik for form state management
 *
 * Used in:
 * - Campaign flow (CampaignInfoStep)
 *
 * @param props - Component props
 * @param props.campaign - Initial campaign form values including name, type, start date, design provided status, files, and click-through URL
 * @param props.campaignTypes - Array of available campaign types with pricing information
 * @param props.handleSubmit - Handler function called when form is submitted with validated values
 * @param props.handleBackClick - Handler function called when back button is clicked
 * @param props.totalCost - Current total cost of the campaign in cents
 * @param props.updateTotalCost - Function to update the total cost when campaign details change
 */
const AdForm = (props: AdFormPropTypes) => {
  const { classes } = useStyles();
  const {
    campaign,
    handleSubmit,
    handleBackClick,
    campaignTypes,
    totalCost,
    updateTotalCost,
    stickyActionsFooter = false,
    showTotal = false,
  } = props;

  const initialCampaignType = campaign.campaignType || campaignTypes[0].typeId;
  const initialCampaignCost = calculateInitialCampaignCost(campaign, campaignTypes);

  useEffect(() => {
    updateTotalCost(initialCampaignCost);
  }, [initialCampaignCost, updateTotalCost]);

  const validationSchema = yupObject({
    campaignName: yupString().required('Campaign Name is required'),
    campaignType: yupString().required('Campaign Length is required'),
    startDate: mixed<AdStartDate>().required('Start date is required'),
    designProvided: yupBool().required('Design source is required'),
    files: array<File>().when('designProvided', {
      is: true,
      then: (schema) => schema.min(1, 'Please provide ad images'),
      otherwise: (schema) =>
        schema.min(
          1,
          'Please provide images, logos or other graphics to be included in ad design',
        ),
    }),
    clickThroughUrl: yupString()
      .test('validURL', 'Please enter a valid click-through URL', (value) => {
        if (!value) {
          return false;
        }

        const isValid = isURL(value);

        return isValid;
      })
      .required('Click-Through URL is required'),
  });

  const initialValues: AdFormValues = {
    ...campaign,
    campaignType: initialCampaignType,
  };

  return (
    <div
      data-component="AdForm"
      className={stickyActionsFooter ? classes.stickyRoot : undefined}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount={!!campaign.campaignType}
      >
        {(props) => (
          <Fields
            onBackClick={handleBackClick}
            campaignTypes={campaignTypes}
            totalCost={totalCost}
            updateTotalCost={updateTotalCost}
            stickyActionsFooter={stickyActionsFooter}
            showTotal={showTotal}
            {...props}
          />
        )}
      </Formik>
    </div>
  );
};

export default AdForm;
